import { useState, useCallback, useEffect, useMemo } from 'react';
import { authenticatedFetch } from '@/lib/api';

export type DeviceType = 'starlink' | 'solark' | 'omni' | 'tesla' | 'hvac';

export interface SystemDevice {
  id: string;
  unit_id: string | null;
  unit_code: string | null;
  last_seen_at: string | null;
  service_line_nickname?: string;
  external_terminal_id?: string;
  connection_status?: string;
  is_active?: boolean;
  external_inverter_id?: string;
  solark_name?: string | null;
  external_device_name?: string;
  external_charger_id?: string;
  /** Shared by Omni (holds the IMEI) and HVAC — separate tables, same column name. */
  external_device_id?: string;
  /** Physical inverters in this Sol-Ark plant (undefined until inventory syncs). */
  inverter_count?: number;
  /** Gateway/datalogger serials. Plural: a two-inverter plant has two gateways. */
  gateway_sns?: string[] | null;
  /** Physical inverter serial numbers for this plant. */
  inverter_sns?: string[] | null;
  /** Tesla QR code (flattened from last_status.qr_code). */
  qr_code?: string | null;
  /** Starlink SSID. */
  ssid?: string;
  /** Starlink service line number. */
  service_line_number?: string;
}

export interface AllDevices {
  starlink: SystemDevice[];
  solark: SystemDevice[];
  omni: SystemDevice[];
  tesla: SystemDevice[];
  hvac: SystemDevice[];
}

interface UseSystemDevicesReturn {
  devices: AllDevices | null;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  setSearchQuery: (q: string) => void;
  showUnlinkedOnly: boolean;
  setShowUnlinkedOnly: (v: boolean) => void;
  filteredDevices: AllDevices | null;
  refresh: () => Promise<void>;
}

function filterDeviceList(devices: SystemDevice[], query: string, unlinkedOnly: boolean): SystemDevice[] {
  let filtered = devices;

  if (unlinkedOnly) {
    filtered = filtered.filter(d => !d.unit_id);
  }

  if (query.length >= 2) {
    const q = query.toLowerCase();
    filtered = filtered.filter(d => {
      const fields = [
        d.service_line_nickname,
        d.external_terminal_id,
        d.external_inverter_id,
        d.solark_name,
        d.external_device_name,
        d.external_device_id,
        d.external_charger_id,
        d.external_device_id,
        d.unit_code,
      ];
      return fields.some(f => f?.toLowerCase().includes(q));
    });
  }

  return filtered;
}

export function useSystemDevices(): UseSystemDevicesReturn {
  const [devices, setDevices] = useState<AllDevices | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(false);

  const fetchDevices = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch('/api/v1/admin/devices/all');
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setDevices(data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch devices';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const filteredDevices = useMemo(() => {
    if (!devices) return null;
    return {
      starlink: filterDeviceList(devices.starlink, searchQuery, showUnlinkedOnly),
      solark: filterDeviceList(devices.solark, searchQuery, showUnlinkedOnly),
      omni: filterDeviceList(devices.omni, searchQuery, showUnlinkedOnly),
      tesla: filterDeviceList(devices.tesla, searchQuery, showUnlinkedOnly),
      hvac: filterDeviceList(devices.hvac, searchQuery, showUnlinkedOnly),
    };
  }, [devices, searchQuery, showUnlinkedOnly]);

  return {
    devices,
    isLoading,
    error,
    searchQuery,
    setSearchQuery,
    showUnlinkedOnly,
    setShowUnlinkedOnly,
    filteredDevices,
    refresh: fetchDevices,
  };
}
